import { createTheme } from "@mui/material/styles";
import type { Theme } from "@mui/material/styles";
// Registers the MuiDataGrid slot on ThemeOptions["components"].
import type {} from "@mui/x-data-grid/themeAugmentation";

import { components } from "./components";
import { darkPalette, lightPalette } from "./palette";
import { typography } from "./typography";
import { RADIUS, SPACING_UNIT } from "./tokens";

/**
 * A deliberately shallow elevation ramp. The old stylesheets used a single
 * `0px 4px 4px rgba(0,0,0,0.25)` shadow for every raised surface, which made
 * dialogs, cards and dropdowns all read at the same depth.
 */
const shadow = (y: number, blur: number, spread: number, alpha: number) =>
  `0px ${y}px ${blur}px ${spread}px rgba(16, 24, 40, ${alpha})`;

const elevation = [
  "none",
  shadow(1, 2, 0, 0.06),
  shadow(1, 3, 0, 0.08),
  shadow(2, 5, -1, 0.09),
  shadow(3, 6, -1, 0.1),
  shadow(4, 8, -2, 0.1),
  shadow(6, 12, -3, 0.11),
  shadow(8, 16, -4, 0.12),
  shadow(10, 20, -5, 0.13),
  shadow(12, 24, -6, 0.14),
  shadow(14, 28, -7, 0.15),
  shadow(16, 32, -8, 0.16),
] as const;

// MUI requires exactly 25 entries; taper the remainder off the last step.
const shadows = Array.from({ length: 25 }, (_, i) =>
  i < elevation.length ? elevation[i] : shadow(16 + i, 32 + i * 2, -8, 0.18)
) as Theme["shadows"];

export const theme = createTheme({
  cssVariables: { colorSchemeSelector: "data-color-scheme" },
  colorSchemes: {
    light: { palette: lightPalette },
    dark: { palette: darkPalette },
  },
  spacing: SPACING_UNIT,
  shape: { borderRadius: RADIUS.sm },
  shadows,
  typography,
  components,
});

export { RADIUS, DRAWER_WIDTH, DRAWER_WIDTH_COLLAPSED } from "./tokens";
export default theme;
